import { FulfillmentTypeGetResponseType } from "@sellar-io/api-v2/types";
import {
  Button,
  DataButton,
  Form,
  H5,
  H6,
  Icon,
  InputCurrency,
  InputContainer,
  BadgeAmber,
} from "@sellar-io/components";
import { useState } from "react";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { useForm, useStore } from "@tanstack/react-form";
import { useFulfillmentTypeUpdate } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeUpdate";
import { FulfillmentTypeContainerDeliveryChargeUpdateDialog } from "../../deliveryCharges/FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeContainerDeliveryChargeUpdateDialog";
import {
  formatCurrency,
  generateFulfillmentTypeContainerDeliveryChargeName,
} from "@sellar-io/common";
import { FulfillmentTypeContainerDeliveryChargeCreateDialog } from "../../deliveryCharges/FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeContainerDeliveryChargeCreateDialog";
import { z } from "zod";
import { getFlatChargeBadge } from "../../../components/courierHaulierSwitchPrompt/pages/PalletDeliverySettingsPage.shared";

export const DeliveryCharges = ({
  deliveryMethod,
  readOnly = false,
}: {
  deliveryMethod?: FulfillmentTypeGetResponseType;
  readOnly?: boolean;
}) => {
  const [deliveryChargeDialogActive, setDeliveryChargeDialogActive] =
    useState(false);

  const initialInputCharge = deliveryMethod?.charge
    ? formatPriceApiToInput(deliveryMethod.charge)
    : "";

  const formSchema = z.object({
    flatCharge: z.string(),
  });

  const form = useForm({
    defaultValues: {
      flatCharge: initialInputCharge,
    },
    onSubmit: ({ value }) => {
      const data = formSchema.parse(value);

      const flatCharge = formatPriceInputToApi(data.flatCharge);
      fulfillmentTypeUpdateMutation.mutate({
        charge: flatCharge,
      });
    },
  });

  const flatCharge = useStore(form.store, (state) => state.values.flatCharge);

  const flatChargeUpdated = flatCharge !== initialInputCharge;

  const fulfillmentTypeUpdateMutation = useFulfillmentTypeUpdate(
    deliveryMethod?.id ?? 0,
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <H5>Flat delivery charge</H5>
          <H6>Apply a flat delivery charge for entire orders</H6>
        </div>
        <Form
          className={"flex gap-2"}
          onSubmit={() => {
            form.handleSubmit();
          }}
        >
          <form.Field name="flatCharge">
            {({ state, handleBlur, handleChange }) => (
              <InputContainer className="py-2">
                <InputCurrency
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e.target.value);
                  }}
                  disabled={readOnly}
                />
                {deliveryMethod?.fulfillmentType === "haulier" && (
                  <span className="flex-none">
                    {getFlatChargeBadge(
                      deliveryMethod.charge ?? undefined,
                      true,
                    )}
                  </span>
                )}
              </InputContainer>
            )}
          </form.Field>
          {!readOnly && (
            <DataButton
              border={true}
              type="submit"
              disabled={!flatChargeUpdated}
              loading={fulfillmentTypeUpdateMutation.isPending}
            >
              Save
            </DataButton>
          )}
        </Form>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <H5>Per-item charges</H5>
            <H6>
              Charge is applied per unit and excludes VAT. Adding multiple units
              of a product will increase delivery charge by the specified
              amount.
            </H6>
          </div>

          {!readOnly && (
            <DataButton
              border={true}
              onClick={() => setDeliveryChargeDialogActive(true)}
            >
              <Icon icon="PLUS" />
              New
            </DataButton>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {deliveryMethod?.FulfillmentTypeContainerDeliveryCharges.length ===
            0 &&
            deliveryMethod.fulfillmentType === "haulier" && (
              <BadgeAmber icon="ALERT">Not set</BadgeAmber>
            )}
          {deliveryMethod?.FulfillmentTypeContainerDeliveryCharges.map(
            (fulfillmentTypeContainerDeliveryCharge) => (
              <DeliveryChargeCard
                key={fulfillmentTypeContainerDeliveryCharge.id}
                fulfillmentTypeContainerDeliveryCharge={
                  fulfillmentTypeContainerDeliveryCharge
                }
                readOnly={readOnly}
              />
            ),
          )}
        </div>
      </div>
      <FulfillmentTypeContainerDeliveryChargeCreateDialog
        active={deliveryChargeDialogActive}
        onDismiss={() => setDeliveryChargeDialogActive(false)}
        fulfillmentType={deliveryMethod?.fulfillmentType ?? "direct"}
        fulfillmentTypeId={deliveryMethod?.id}
      />
    </>
  );
};

const DeliveryChargeCard = ({
  fulfillmentTypeContainerDeliveryCharge,
  readOnly = false,
}: {
  fulfillmentTypeContainerDeliveryCharge: FulfillmentTypeGetResponseType["FulfillmentTypeContainerDeliveryCharges"][number];
  readOnly?: boolean;
}) => {
  const [active, setActive] = useState(false);

  return (
    <>
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-white px-4 py-2 hover:bg-zinc-50"
        onClick={() => setActive(true)}
      >
        <p>
          {formatCurrency(fulfillmentTypeContainerDeliveryCharge.charge)}{" "}
          delivery charge for{" "}
          {generateFulfillmentTypeContainerDeliveryChargeName({
            volume: fulfillmentTypeContainerDeliveryCharge.volume,
            packQuantity: fulfillmentTypeContainerDeliveryCharge.packQuantity,
            displayUnit: fulfillmentTypeContainerDeliveryCharge.displayUnit,
            containerType: fulfillmentTypeContainerDeliveryCharge.containerType,
          })}
        </p>
        {!readOnly && (
          <Button
            color="transparent"
            size="small"
            className="flex items-center gap-2"
          >
            Edit <Icon icon="CHEVRON_RIGHT" />
          </Button>
        )}
      </div>
      <FulfillmentTypeContainerDeliveryChargeUpdateDialog
        active={active}
        onDismiss={() => setActive(false)}
        fulfillmentTypeContainerDeliveryCharge={
          fulfillmentTypeContainerDeliveryCharge
        }
      />
    </>
  );
};
